import i18n from '@src/core/i18n';
import { IMfaErrorBody } from '@src/features/auth/redux/types';

// RTK's axiosBaseQuery returns errors as { status, data } where data is the response body.
export interface IRtkError {
  status?: number;
  data?: unknown;
}

export const MFA_ERROR_CODE = {
  CODE_INVALID: 'MFA_CODE_INVALID',
  TOKEN_INVALID: 'MFA_TOKEN_INVALID',
  LOCKED: 'MFA_LOCKED',
  MANDATORY: 'MFA_MANDATORY',
  ENROLLMENT_REQUIRED: 'MFA_ENROLLMENT_REQUIRED',
} as const;

export const asRtkError = (err: unknown): IRtkError => {
  if (err && typeof err === 'object' && 'status' in err) {
    return err as IRtkError;
  }
  return {};
};

export const getMfaErrorBody = (err: unknown): IMfaErrorBody => {
  const data = asRtkError(err).data;
  if (data && typeof data === 'object') {
    return data as IMfaErrorBody;
  }
  return {};
};

export const getMfaErrorCode = (err: unknown): string | undefined => getMfaErrorBody(err).code;

// Maps a backend MFA error code to a localized, user-facing message. Falls back to a
// generic "could not verify" message for unknown / missing codes. Translations live in the
// `mfa.errors.*` namespace (src/core/i18n/locales/*.json).
export const getMfaErrorMessage = (err: unknown): string => {
  const code = getMfaErrorCode(err);

  switch (code) {
  case MFA_ERROR_CODE.CODE_INVALID:
    return i18n.t('mfa.errors.MFA_CODE_INVALID');
  case MFA_ERROR_CODE.TOKEN_INVALID:
    return i18n.t('mfa.errors.MFA_TOKEN_INVALID');
  case MFA_ERROR_CODE.LOCKED: {
    // The lockout message includes a {{seconds}} countdown; fall back to 30s
    // (the backend default) when the body omits retryAfterSeconds.
    const seconds = getMfaErrorBody(err).retryAfterSeconds ?? 30;
    return i18n.t('mfa.errors.MFA_LOCKED', { seconds });
  }
  case MFA_ERROR_CODE.MANDATORY:
    return i18n.t('mfa.errors.MFA_MANDATORY');
  case MFA_ERROR_CODE.ENROLLMENT_REQUIRED:
    return i18n.t('mfa.errors.MFA_ENROLLMENT_REQUIRED');
  default:
    return i18n.t('mfa.errors.generic');
  }
};
